import { useEffect, useState, useContext } from "react";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import * as S from "./styles";

import {
  CircularProgress,
  ClickAwayListener,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Tooltip,
  useMediaQuery,
  useTheme,
  Box,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { FutButton } from "@/components/FutButton";
import { infoData, selectData } from "./data";
import RoundInput from "@/components/RoundInput";
import PatternInput from "@/components/PatternInput";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import FutSelect from "@/components/FutSelect";
import * as yup from "yup";
import {
  PASSWORD_MATCH,
  REQUIRED_FIELD,
  VALID_CPF,
  VALID_EMAIL,
  VALID_PHONE,
} from "@/constants/yupErrors";
import { cpfIsValid } from "@/utils/cpfValidator";
import { phoneRegex } from "@/utils/regex";
import { AiOutlineClose, AiOutlineCloseCircle } from "react-icons/ai";
import {
  HiOutlineCreditCard,
  HiOutlineInformationCircle,
} from "react-icons/hi2";
import { yupResolver } from "@hookform/resolvers/yup";

import { api } from "@/services/api";
import { toast } from "react-toastify";
import { catchError } from "@/utils/catchError";
import { useAuth } from "@/context/AuthContext";
import { format } from "date-fns";
import AdAppCoupon from "@/components/AdAppCoupon";
import { NumericFormat } from "react-number-format";
import { WithdrawHistoryType, User } from "@/interfaces/user";
import { UseUser } from "@/context/UserContext";
import { BsCheck2Circle } from "react-icons/bs";
import { PiWarningCircleBold } from "react-icons/pi";
import { TAXES } from "@/constants/paymentTax";
import { BiEdit } from "react-icons/bi";
import PerfilForm from "./components/PerfilForm";
import { formatMoney } from "@/utils/formatMoney";

const personalSchema = yup.object({
  name: yup.string().required(REQUIRED_FIELD),
  email: yup.string().required(REQUIRED_FIELD).trim().email(VALID_EMAIL),
  phone: yup.string().required(REQUIRED_FIELD).matches(phoneRegex, VALID_PHONE),
  cpf: yup
    .string()
    .required(REQUIRED_FIELD)
    .test(
      "is-cpf-valid",
      () => VALID_CPF,
      (value) => cpfIsValid(value)
    ),
  cep: yup.string().required(REQUIRED_FIELD),
  state: yup.string().required(REQUIRED_FIELD),
  city: yup.string().required(REQUIRED_FIELD),
  address: yup.string().required(REQUIRED_FIELD),
  pix_key: yup.string().required(REQUIRED_FIELD),
});

const creditCardSchema = yup.object({
  creditCard: yup.string().required(REQUIRED_FIELD),
  validDate: yup.string().required(REQUIRED_FIELD),
  cvv: yup.string().required(REQUIRED_FIELD),
  cardName: yup.string().required(REQUIRED_FIELD),
});

const passSchema = yup.object({
  oldPassword: yup.string().required(REQUIRED_FIELD),
  newPassword: yup.string().required(REQUIRED_FIELD),
  repeatPass: yup
    .string()
    .required(REQUIRED_FIELD)
    .oneOf([yup.ref("newPassword")], PASSWORD_MATCH),
});

export type IPersonalForm = yup.InferType<typeof personalSchema> & {
  confirmed: boolean;
};
type ICreditCardForm = yup.InferType<typeof creditCardSchema>;
type IPassForm = yup.InferType<typeof passSchema>;

export default function Perfil() {
  const theme = useTheme();
  const IsMedia1200 = !useMediaQuery(theme.breakpoints.up("lg"));
  const { changeToken, decodedToken } = useAuth();
  const { balance, refreshBalance } = UseUser();

  const [isEditable, setIsEditable] = useState(false);
  const [tooltip, setTooltip] = useState(false);
  const [generalLoading, setGeneralLoading] = useState(true);
  const [personalData, setPersonalData] = useState<IPersonalForm>(
    {} as IPersonalForm
  );
  const [nextCharge, setNextCharge] = useState("Carregando...");
  const [withdrawHistory, setWithdrawHistory] = useState<WithdrawHistoryType[]>(
    []
  );
  const [withdrawLoading, setWithDrawLoading] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);

  const [planModal, setPlanModal] = useState(false);
  const [subscriptionModal, setSubscriptionModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [changeCreditCard, setChangeCreditCard] = useState(false);
  const [changePassModal, setChangePassModal] = useState(false);
  const [withdrawMoney, setWithdrawMoney] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [editStatus, setEditStatus] = useState(false);
  const [withdrawValue, setWithdrawValue] = useState(0);

  const personalForm = useForm<IPersonalForm>({
    defaultValues: {
      name: "",
      cpf: "",
      email: "",
      phone: "",
      pix_key: "",
      address: "",
      cep: "",
      state: "",
      city: "",
    },
    resolver: yupResolver(personalSchema),
  });

  const getInitialData = async () => {
    try {
      const response = await api.get(`/user/get/${decodedToken.id}`);

      if (!response || !response.data || !response.data.body) {
        throw new Error("Resposta inválida do servidor");
      }

      const initialData = response.data.body;

      setPersonalData(initialData);

      // Check if UserCharge array exists and has at least one item
      if (initialData.UserCharge && initialData.UserCharge.length > 0 && initialData.UserCharge[0].next_charge_date) {
        setNextCharge(
          format(
            new Date(initialData.UserCharge[0].next_charge_date),
            "dd/MM/yyyy"
          )
        );
      } else {
        setNextCharge("Data não disponível");
      }

      setGeneralLoading(false);
    } catch (e) {
      console.error("Error fetching user data:", e);
      toast.error("Ocorreu um erro ao tentar buscar os dados");
      setGeneralLoading(false);
    }
  };

  const getWithdrawHist = async () => {
    try {
      setWithDrawLoading(true);
      const requestWithdrawHistory = await api.get(
        `/payment/withdraw-history/${decodedToken.id}`
      );

      if (requestWithdrawHistory && requestWithdrawHistory.data && requestWithdrawHistory.data.body) {
        setWithdrawHistory(requestWithdrawHistory.data.body.data || []);
      } else {
        setWithdrawHistory([]);
      }

      setWithDrawLoading(false);
    } catch (e) {
      console.error("Error fetching withdraw history:", e);
      toast.error("Ocorreu um erro ao tentar buscar seu histórico de saque");
      setWithDrawLoading(false);
      setWithdrawHistory([]);
    }
  };

  useEffect(() => {
    if (decodedToken?.id) {
      getWithdrawHist();
      getInitialData();
    }
  }, [decodedToken?.id]);

  useEffect(() => {
    if (personalData && Object.keys(personalData).length > 0) {
      personalForm.reset({ ...personalData });
    }
  }, [personalData]);

  const creditCardForm = useForm<ICreditCardForm>({
    defaultValues: {
      cardName: "",
      creditCard: "",
      cvv: "",
      validDate: "",
    },
    resolver: yupResolver(creditCardSchema),
  });

  const changePassForm = useForm<IPassForm>({
    resolver: yupResolver(passSchema),
  });

  const withdrawForm = useForm({
    defaultValues: {
      money: "",
    },
  });

  const creditCardError = creditCardForm.formState.errors;

  const changePassError = changePassForm.formState.errors;
  const personalFormError = personalForm.formState.errors;

  const planHandler = (planType: string) => { };

  const sendVerification = async () => {
    try {
      setSendEmail(true);
      await api.post("/user/requestEmailConfirmation", {
        email: decodedToken.email,
      });
      toast.success("Email enviado com sucesso!");
    } catch (e) {
      setSendEmail(false);
      const message = catchError(e);
      if (message) {
        toast.error(message);
        return;
      }
      toast.error("Ocorreu um erro inesperado ao tentar enviar o email");
    }
  };

  const handleWithdrawMoney = () => {
    if (!balance) {
      toast.error("Não foi possível verificar seu saldo. Tente novamente mais tarde.");
      return;
    }

    const balanceValue = typeof balance.balance === "string"
      ? parseFloat(balance.balance)
      : balance.balance;

    if (balanceValue < 0) {
      toast.error(
        "Você não tem saldo o suficiente para sacar. Mínimo de R$ 50,00"
      );
      return;
    }

    if (!personalData || !personalData.confirmed) {
      toast.error("Por favor, confirme seu email antes de sacar");
      return;
    }

    const { cep, address, city, pix_key, state } = personalData;

    if (cep && address && pix_key && state && city) {
      setWithdrawMoney(true);
      return;
    }

    toast.error(
      "Por favor preencha todas suas informações pessoais, incluindo o pix antes de sacar seu dinheiro"
    );
  };

  const submitWithdrawMoney = async () => {
    try {
      const rawValue = withdrawForm.getValues("money");

      if (!rawValue) {
        toast.error("Por favor, insira um valor para saque");
        return;
      }

      const valueToWithdraw = parseFloat(rawValue);

      if (isNaN(valueToWithdraw) || valueToWithdraw <= 0) {
        toast.error("Por favor, insira um valor válido para saque");
        return;
      }

      // Safely calculate available balance
      const balanceValue = typeof balance.balance === "string"
        ? parseFloat(balance.balance)
        : balance.balance;

      const moneyAccount = balanceValue !== 0
        ? (Math.floor(balanceValue * TAXES))
        : 0;

      if (valueToWithdraw > moneyAccount) {
        toast.error("Você não tem todo esse dinheiro para sacar");
        return;
      }

      setGeneralLoading(true);

      const response = await api.post(`/payment/withdraw/${decodedToken.id}`, {
        amount: valueToWithdraw,
      });

      if (!response?.data) {
        throw new Error("Resposta inválida do servidor");
      }

      await refreshBalance(decodedToken.id);
      await getWithdrawHist();

      setWithdrawMoney(false);
      toast.success(
        "Sua solicitação para sacar foi recebida, em breve o valor estará em seu bolso!",
        { autoClose: 15000 }
      );
    } catch (e) {
      console.error("Error processing withdrawal:", e);
      const message = catchError(e);
      if (message) {
        toast.error(message);
        return;
      }
      toast.error(
        "Ocorreu um erro ao tentar sacar o dinheiro, veja se a sua chave pix está correta",
        { autoClose: 15000 }
      );
    } finally {
      setWithdrawMoney(false);
      setGeneralLoading(false);
    }
  };

  const submitEdit: SubmitHandler<IPersonalForm> = async (data) => {
    try {
      if (window.confirm("Deseja mudar suas informações?")) {
        setGeneralLoading(true);
        const { confirmed, ...personalDataClone } = { ...personalData };

        Object.keys(personalData).map((el) => {
          const key = el as keyof typeof personalDataClone;
          if (data[key]) {
            personalDataClone[key] = data[key];
            return;
          }
        });

        personalDataClone.cpf = personalDataClone.cpf.replace(/[^\d]/g, "");

        const { address, cep, city, cpf, email, name, phone, pix_key, state } =
          personalDataClone;

        const apiChanges = await api.put(`/user/edit/${decodedToken.id}`, {
          address,
          cep,
          city,
          cpf,
          email,
          name,
          phone,
          pix_key,
          state,
        });

        setPersonalData(apiChanges.data.body);
        setGeneralLoading(false);
        setIsEditable(false);
      }
      setIsEditable(false);
      return;
    } catch (e) {
      toast.error("Ocorreu um erro ao tentar mudar as informações");
      setGeneralLoading(false);
      setIsEditable(false);
    }
  };

  const submitCreditCard: SubmitHandler<ICreditCardForm> = (data) => { };

  const cancelSubscription = () => {
    try {
      api.delete(`/payment/cancelSubscription?userId=${decodedToken.id}`);
      setCancelModal(false);
      toast.success(
        "Sua assinatura foi cancelada e não será cobrado a próxima fatura."
      );
    } catch (e) {
      toast.error("Ocorreu um erro ao tenta cancelar a assinatura");
    }
  };

  const submitChangePass: SubmitHandler<IPassForm> = async (data) => {
    try {
      const { repeatPass, ...dataClone } = data as typeof data & {
        email?: string;
      };

      dataClone.email = decodedToken.email;

      const response = await api.put("/user/reset", dataClone);

      const { body } = response.data;

      changeToken(body.token);
      toast.success("Senha Trocada Com Sucesso!");
      setChangePassModal(false);
    } catch (e) {
      const message = catchError(e);
      if (message) {
        toast.error(message);
        return;
      }
      toast.error("Ocorreu um erro ao tentar mudar a senha");
    }
  };

  const withdrawMoneyHandler = async () => {
    try {
      setIsLoading(true);
      await api.post(`/payment/withdraw/${decodedToken.id}`, {
        amount: withdrawValue,
      });
      toast.success("Solicitação de saque enviada com sucesso");
      window.location.reload();
    } catch (e: any) {
      toast.error(
        e.response?.data?.error ||
        "Ocorreu um erro ao tentar solicitar o saque"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <S.FormHeader>
          <Typography variant="h5">Meu Perfil</Typography>
          {!editStatus ? (
            <IconButton onClick={() => setEditStatus(true)}>
              <BiEdit />
            </IconButton>
          ) : null}
        </S.FormHeader>

        {!editStatus ? (
          <>
            <Box sx={{ padding: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <S.InputLabelWrapper>
                    <Typography variant="caption">Nome completo</Typography>
                    <Typography>{personalData?.name || "Não informado"}</Typography>
                  </S.InputLabelWrapper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <S.InputLabelWrapper>
                    <Typography variant="caption">Email</Typography>
                    <Typography>{personalData?.email || "Não informado"}</Typography>
                  </S.InputLabelWrapper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <S.InputLabelWrapper>
                    <Typography variant="caption">CPF</Typography>
                    <Typography>{personalData?.cpf || "Não informado"}</Typography>
                  </S.InputLabelWrapper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <S.InputLabelWrapper>
                    <Typography variant="caption">Chave Pix</Typography>
                    <Typography>{personalData?.pix_key || "Não informado"}</Typography>
                  </S.InputLabelWrapper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <S.InputLabelWrapper>
                    <Typography variant="caption">Telefone</Typography>
                    <Typography>{personalData?.phone || "Não informado"}</Typography>
                  </S.InputLabelWrapper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <S.InputLabelWrapper>
                    <Typography variant="caption">CEP</Typography>
                    <Typography>{personalData?.cep || "Não informado"}</Typography>
                  </S.InputLabelWrapper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <S.InputLabelWrapper>
                    <Typography variant="caption">Estado</Typography>
                    <Typography>{personalData?.state || "Não informado"}</Typography>
                  </S.InputLabelWrapper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <S.InputLabelWrapper>
                    <Typography variant="caption">Cidade</Typography>
                    <Typography>{personalData?.city || "Não informado"}</Typography>
                  </S.InputLabelWrapper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <S.InputLabelWrapper>
                    <Typography variant="caption">Endereço</Typography>
                    <Typography>{personalData?.address || "Não informado"}</Typography>
                  </S.InputLabelWrapper>
                </Grid>
              </Grid>
            </Box>
          </>
        ) : (
          <PerfilForm
            user={personalData as any}
            setEditStatus={setEditStatus}
            editStatus={editStatus}
          />
        )}
      </Paper>
      <S.Spacer />
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h5">Histórico de saques</Typography>
              <Divider
                sx={{ margin: "10px 0 20px 0", borderColor: "primary.main" }}
              />
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Valor</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Data de Pagamento</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {withdrawLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">Carregando...</TableCell>
                      </TableRow>
                    ) : withdrawHistory && withdrawHistory.length > 0 ? (
                      withdrawHistory.map((req) => {
                        return (
                          <TableRow key={req.id}>
                            <TableCell>
                              {format(
                                new Date(req.created_at),
                                "dd/MM/yyyy HH:mm"
                              )}
                            </TableCell>
                            <TableCell>R$ {req.amount.toFixed(2)}</TableCell>
                            <TableCell>{req.status}</TableCell>
                            <TableCell>
                              {req.payment_date
                                ? format(
                                  new Date(req.payment_date),
                                  "dd/MM/yyyy"
                                )
                                : "-"}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Nenhum histórico encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h5">Realizar Saque</Typography>
              <Divider
                sx={{ margin: "10px 0 20px 0", borderColor: "primary.main" }}
              />
              <Stack spacing={2}>
                <RoundInput
                  type="number"
                  fullWidth
                  value={withdrawValue}
                  onChange={(e) => setWithdrawValue(Number(e.target.value))}
                  placeholder="Insira o valor"
                  topLabel="Valor do saque"
                />
                <FutButton
                  onClick={withdrawMoneyHandler}
                  disabled={!withdrawValue || withdrawValue <= 0 || isLoading}
                  isLoading={isLoading}
                >
                  Solicitar saque
                </FutButton>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Modal open={withdrawMoney} onClose={() => setWithdrawMoney(false)}>
        <S.PlanWrapperModal>
          <S.WithdrawContainer>
            <S.WithdrawHeader>
              <p>
                Saldo em conta:{" "}
                {balance?.balance === undefined || balance?.balance === ""
                  ? "Carregando..."
                  : new Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(Math.floor((typeof balance.balance === 'number' ? balance.balance : parseFloat(String(balance.balance))) * TAXES) || 0)}
              </p>
              <span onClick={() => setWithdrawMoney(false)}>
                <AiOutlineClose />
              </span>
            </S.WithdrawHeader>
            <Controller
              name="money"
              control={withdrawForm.control}
              render={({ field: { onChange, ...fields } }) => (
                <NumericFormat
                  {...fields}
                  onValueChange={(value) => {
                    onChange(value.value);
                  }}
                  thousandSeparator="."
                  decimalSeparator=","
                  prefix="R$"
                  decimalScale={2}
                  customInput={RoundInput}
                  size="small"
                />
              )}
            />
            <FutButton
              fullWidth
              isLoading={generalLoading}
              onClick={submitWithdrawMoney}
            >
              Sacar Dinheiro
            </FutButton>
          </S.WithdrawContainer>
        </S.PlanWrapperModal>
      </Modal>

      <Modal open={changePassModal} onClose={() => setChangePassModal(false)}>
        <S.PlanWrapperModal>
          <S.ChangePassContainer>
            <S.HeaderCreditModal>
              <p>Trocar Senha</p>
              <span onClick={() => setChangePassModal(false)}>
                <AiOutlineClose />
              </span>
            </S.HeaderCreditModal>
            <S.ChangePassInputs
              onSubmit={changePassForm.handleSubmit(submitChangePass)}
            >
              <RoundInput
                inputName="oldPassword"
                control={changePassForm.control}
                type="password"
                size="small"
                fullWidth
                topLabel="Senha Antiga"
                errorText={changePassError.oldPassword}
              />
              <RoundInput
                inputName="newPassword"
                control={changePassForm.control}
                type="password"
                size="small"
                fullWidth
                topLabel="Senha Nova"
                errorText={changePassError.newPassword}
              />
              <RoundInput
                inputName="repeatPass"
                control={changePassForm.control}
                type="password"
                size="small"
                fullWidth
                topLabel="Confirmar Senha Nova"
                errorText={changePassError.repeatPass}
              />
              <FutButton fullWidth sx={{ mt: 4 }} type="submit">
                Salvar
              </FutButton>
            </S.ChangePassInputs>
          </S.ChangePassContainer>
        </S.PlanWrapperModal>
      </Modal>

      <Modal
        open={planModal}
        sx={{ overflow: "auto" }}
        onClose={() => setPlanModal((prev) => !prev)}
      >
        <S.PlanWrapperModal>
          <S.PlanContainerModal>
            <h2>Plano</h2>

            <FutButton
              variant="text"
              fullWidth
              onClick={() => setCancelModal((prev) => !prev)}
              sx={{ color: "red", borderColor: "red", mt: 3 }}
            >
              Cancelar Assinatura
            </FutButton>
            <FutButton
              fullWidth
              sx={{ mt: 1 }}
              onClick={() => setPlanModal(false)}
            >
              Voltar
            </FutButton>
          </S.PlanContainerModal>

          <Modal
            sx={{ overflow: "auto" }}
            open={subscriptionModal}
            onClose={() => setSubscriptionModal(false)}
          >
            <S.PlanWrapperModal>
              <S.OurPlansWrapper>
                <h2>Nossos Planos</h2>
                <p>Escolha o seu plano</p>
                <S.PlansButton onClick={() => planHandler("monthly")}>
                  <div>
                    <span>Mensal</span>
                    <p>R$ 99,00/mês</p>
                  </div>
                </S.PlansButton>
                <S.PlansButton onClick={() => planHandler("semiannual")}>
                  <div>
                    <span>Semestral</span>
                    <p>R$ 79,00/6 meses</p>
                  </div>
                  <S.DiscountTag>
                    <p>20% de desconto</p>
                  </S.DiscountTag>
                </S.PlansButton>
                <S.PlansButton onClick={() => planHandler("annual")}>
                  <div>
                    <span>Anual</span>
                    <p>R$ 69,00/12 meses</p>
                  </div>
                  <S.DiscountTag>
                    <p>25% de desconto</p>
                  </S.DiscountTag>
                </S.PlansButton>
                <S.CloseButtonCont onClick={() => setSubscriptionModal(false)}>
                  <AiOutlineClose />
                </S.CloseButtonCont>
              </S.OurPlansWrapper>
            </S.PlanWrapperModal>
          </Modal>

          <Modal
            open={cancelModal}
            sx={{ overflow: "auto" }}
            onClose={() => setCancelModal(false)}
          >
            <S.PlanWrapperModal>
              <S.CancelSubscriptionCont onClick={cancelSubscription}>
                <p>Cancelar assinatura</p>

                <p>
                  Você tem certeza que deseja cancelar a sua assinatura? Você
                  irá perder todos os seus benefícios oferecidos pela Futscore ao
                  final da sua assinatura.
                </p>

                <div>
                  <small>Sua assinatura terminará no dia:</small>
                  <p>{nextCharge}</p>
                </div>
                <S.CancelSubscriptionAction>
                  <FutButton
                    sx={{ flexBasis: "50%" }}
                    onClick={cancelSubscription}
                  >
                    Voltar
                  </FutButton>
                  <FutButton
                    variant="text"
                    sx={{ flexBasis: "50%", color: "red", borderColor: "red" }}
                  >
                    Cancelar Assinatura
                  </FutButton>
                </S.CancelSubscriptionAction>
              </S.CancelSubscriptionCont>
            </S.PlanWrapperModal>
          </Modal>

          <Modal
            open={changeCreditCard}
            sx={{ overflow: "auto" }}
            onClose={() => setChangeCreditCard(false)}
          >
            <S.PlanWrapperModal>
              <S.CreditCardModal>
                <S.HeaderCreditModal>
                  <p>Pagamento</p>
                  <span onClick={() => setChangeCreditCard(false)}>
                    <AiOutlineClose />
                  </span>
                </S.HeaderCreditModal>
                <S.CreditCardInputs
                  onSubmit={creditCardForm.handleSubmit(submitCreditCard)}
                >
                  <PatternInput
                    inputName="creditCard"
                    format="#### #### #### ####"
                    mask="_"
                    control={creditCardForm.control}
                    errorText={creditCardError.creditCard}
                    topLabel="Número do cartão de crédito"
                    size="small"
                    endAdornment={
                      <InputAdornment position="end">
                        <HiOutlineCreditCard />
                      </InputAdornment>
                    }
                    fullWidth
                  />
                  <S.JoinInputs>
                    <PatternInput
                      inputName="validDate"
                      control={creditCardForm.control}
                      errorText={creditCardError.validDate}
                      format="##/##"
                      mask="_"
                      fullWidth
                      topLabel="Data da validade"
                      size="small"
                    />
                    <PatternInput
                      inputName="cvv"
                      control={creditCardForm.control}
                      errorText={creditCardError.cvv}
                      format="###"
                      mask="_"
                      fullWidth
                      topLabel="CVV"
                      size="small"
                    />
                  </S.JoinInputs>

                  <RoundInput
                    inputName="cardName"
                    control={creditCardForm.control}
                    topLabel="Nome do Cartão"
                    errorText={creditCardError.cardName}
                    fullWidth
                    size="small"
                  />

                  <S.ChangePlan>
                    <div>
                      <p>Semestral</p>
                      <p>
                        R$79,00<span>/6 meses</span>
                      </p>
                    </div>
                    <div>
                      <FutButton
                        variant="text"
                        onClick={() => setSubscriptionModal((prev) => !prev)}
                      >
                        Trocar de plano
                      </FutButton>
                    </div>
                  </S.ChangePlan>
                  <S.TermOfServices>
                    <small>
                      Ao clicar no botão abaixo, você concorda com os Termos de
                      Uso e a Declaração de Privacidade da Futscore. Confirma
                      também ser maior de 18 anos e autoriza a renovação
                      automática da sua assinatura, com cobrança recorrente na
                      sua forma de pagamento. O cancelamento pode ser realizado
                      a qualquer momento para evitar cobranças futuras.
                    </small>
                  </S.TermOfServices>
                  <FutButton fullWidth type="submit">
                    Atualizar Forma de Pagamento
                  </FutButton>
                </S.CreditCardInputs>
              </S.CreditCardModal>
            </S.PlanWrapperModal>
          </Modal>
        </S.PlanWrapperModal>
      </Modal>
    </>
  );
}
